import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Briefcase, Users, User, Building2, MapPin } from 'lucide-react';
import { countries } from '../utils/countries';

const GoogleRoleSelectionModal = ({ isOpen, onClose, onSubmit, userData }) => {
  const [selectedRole, setSelectedRole] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [loading, setLoading] = useState(false);

  // Check if freelance is selected
  const isFreelance = selectedRole === 'freelance';

  // Pre-fill first and last name from Google data when userData changes
  useEffect(() => {
    if (userData?.givenName) {
      setFirstName(userData.givenName);
    }
    if (userData?.familyName) {
      setLastName(userData.familyName);
    }
  }, [userData]);

  const handleSubmit = async () => {
    if (!selectedRole || !firstName.trim() || !lastName.trim() || !companyName.trim() || !city.trim() || !country) {
      return;
    }
    
    setLoading(true);
    await onSubmit({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      // Freelance maps to resource_manager with a special flag
      role: selectedRole === 'freelance' ? 'resource_manager' : selectedRole,
      is_freelance: selectedRole === 'freelance',
      company_name: companyName.trim(),
      city: city.trim(),
      country: country
    });
    setLoading(false);
  };

  if (!isOpen) return null;

  // Role options configuration - same as WelcomeRegistrationModal
  const roleOptions = [
    {
      id: 'project_manager',
      icon: Briefcase,
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-500',
      hoverBorder: 'hover:border-blue-400',
      title: "I'm looking for consultants for my projects",
      subtitle: 'Project Manager (PM)',
      description: 'Post project needs and find the best ServiceNow consultants'
    },
    {
      id: 'resource_manager',
      icon: Users,
      iconColor: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-500',
      hoverBorder: 'hover:border-emerald-400',
      title: 'I offer available consultants',
      subtitle: 'Resource Manager (RM)',
      description: "Showcase your team's consultants and find matching projects"
    },
    {
      id: 'freelance',
      icon: User,
      iconColor: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-500',
      hoverBorder: 'hover:border-purple-400',
      title: 'I am a freelance consultant',
      subtitle: 'Independent Consultant',
      description: 'Available for new ServiceNow projects as an independent professional'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="text-center mb-6">
          <img 
            src="/snow-exchange-logo.png" 
            alt="Snow Exchange" 
            className="w-16 h-16 mx-auto mb-4 rounded-xl"
          />
          <h2 className="text-2xl font-bold mb-2 text-gray-900">
            Welcome to Snow Exchange!
          </h2>
          <p className="text-gray-500">
            Tell us about yourself to get started
          </p>
        </div>

        {/* First Name & Last Name */}
        <div className="mb-5 grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="google-first-name" className="mb-1.5 block text-sm font-medium text-gray-700">
              First name *
            </Label>
            <Input
              id="google-first-name"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="John"
              className="w-full"
              data-testid="google-first-name"
            />
          </div>
          <div>
            <Label htmlFor="google-last-name" className="mb-1.5 block text-sm font-medium text-gray-700">
              Last name *
            </Label>
            <Input
              id="google-last-name"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Doe"
              className="w-full"
              data-testid="google-last-name"
            />
          </div>
        </div>

        {/* Role Selection */}
        <div className="mb-5">
          <Label className="mb-2 block text-sm font-medium text-gray-700">What describes you best? *</Label>
          <div className="space-y-2.5">
            {roleOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = selectedRole === option.id;
              
              return (
                <button
                  key={option.id}
                  onClick={() => setSelectedRole(option.id)}
                  data-testid={`google-role-${option.id}`}
                  className={`w-full p-4 border-2 rounded-xl text-left transition-all duration-200 ${option.hoverBorder} ${
                    isSelected
                      ? `${option.borderColor} ${option.bgColor} shadow-sm`
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isSelected ? option.bgColor : 'bg-gray-100'
                    }`}>
                      <Icon className={`w-5 h-5 ${isSelected ? option.iconColor : 'text-gray-500'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 mb-0.5">
                        {option.title}
                      </div>
                      <div className="text-xs text-gray-500 mb-1">
                        {option.subtitle}
                      </div>
                      <div className="text-xs text-gray-400">
                        {option.description}
                      </div>
                    </div>
                    {/* Selection indicator */}
                    <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${
                      isSelected 
                        ? `${option.borderColor} ${option.bgColor}` 
                        : 'border-gray-300'
                    }`}>
                      {isSelected && (
                        <div className={`w-2.5 h-2.5 rounded-full ${
                          option.id === 'project_manager' ? 'bg-blue-500' :
                          option.id === 'resource_manager' ? 'bg-emerald-500' : 'bg-purple-500'
                        }`} />
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Company Name - changes label based on freelance */}
        <div className="mb-5">
          <Label htmlFor="google-company-name" className="mb-1.5 block text-sm font-medium text-gray-700 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-gray-400" />
            {isFreelance ? 'Your business name *' : 'Company name *'}
          </Label>
          <Input
            id="google-company-name"
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder={isFreelance ? "e.g., John Doe Consulting" : "e.g., Acme Consulting"}
            className="w-full"
            data-testid="google-company-name"
          />
          {isFreelance && (
            <p className="text-xs text-gray-400 mt-1">
              This can be your personal brand name or trading name
            </p>
          )}
        </div>

        {/* City and Country */}
        <div className="mb-6 grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="google-city" className="mb-1.5 block text-sm font-medium text-gray-700 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              City *
            </Label>
            <Input
              id="google-city"
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g., Paris"
              className="w-full"
              data-testid="google-city"
            />
          </div>
          <div>
            <Label htmlFor="google-country" className="mb-1.5 block text-sm font-medium text-gray-700">
              Country *
            </Label>
            <select
              id="google-country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              data-testid="google-country"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
            >
              <option value="">Select country</option>
              {countries.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            onClick={handleSubmit}
            disabled={!selectedRole || !firstName.trim() || !lastName.trim() || !companyName.trim() || !city.trim() || !country || loading}
            className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-md"
            data-testid="google-submit"
          >
            {loading ? 'Creating...' : 'Get Started'}
          </Button>
          <Button
            onClick={onClose}
            variant="outline"
            disabled={loading}
            data-testid="google-cancel"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GoogleRoleSelectionModal;
